import { chargerActions } from '../slices/charger-slice';
import { PrepareApiProcedure } from '../../components/common/utils/prepare-api-procedure';
import {
  ApiRequest,
  fetchDataFromAPI,
  fetchDataFromAPITyped,
  isSuccessful,
} from '../../services/api-requests';
import { ChargerAttributes } from '../../models/charger-model';
import { notificationService } from '../../services/notification-service';
import {
  getCurrentUtcSeconds,
  getPastUtcSeconds,
} from '../../components/common/utils/date-time-conversions';
import * as Location from 'expo-location';
import { AppDispatch, RootState } from '..';

/**
 * Represents a schedule for an EVSE charger.
 */
export interface ChargerSchedule {
  scheduleId: string;
  startTime: string; // ISO 8601 format
  endTime: string; // ISO 8601 format
  device_id?: string;
  chargerId?: string;
  orderId?: string;
  max_kwh?: number;
  createdAt?: string;
  updatedAt?: string;
  message?: string;
}

export interface SendSchedule {
  start_time: string;
  end_time: string;
  chargerId: string;
  orderId: string;
  max_kwh?: number | null;
}

/**
 * Response for DELETE schedules.
 */
interface DeleteSchedulesResponse {
  success: string[]; // list of schedule_ids that were deleted
  error: Array<{
    schedule_id: string;
    message: string;
    error_code: string;
  }>;
}

/**
 * Retrieves schedules for one or more devices.
 *
 * @param deviceIds - A single device ID or an array of device IDs.
 */
export const getSchedule = async (
  deviceIds: string | string[]
): Promise<ChargerSchedule | null> => {
  try {
    // Convert array to comma-separated string if needed.
    const deviceIdsParam = Array.isArray(deviceIds)
      ? deviceIds.join(',')
      : deviceIds;

    const apiQuery: ApiRequest = {
      route: `charger/schedule/get/${deviceIdsParam}`,
      request: {
        method: 'GET',
        entity: 'schedule',
        body: undefined,
      },
    };

    const response = await fetchDataFromAPITyped<ChargerSchedule>(apiQuery);
    if (!response || !isSuccessful(response.status)) {
      notificationService.sendNotification('error', 'Failed to get schedule');
      return null;
    } else {
      return response.payload;
    }
  } catch (error) {
    console.error('Error while fetching schedule', error);
    notificationService.sendNotification(
      'error',
      `Error while fetching schedule: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return null;
  }
};

/**
 * Creates or updates a schedule.
 *
 * @param schedule - A schedule object to be created or updated.
 */
export const setSchedule = async (
  schedule: SendSchedule
): Promise<ChargerSchedule | ChargerSchedule[] | null> => {
  try {
    const apiQuery: ApiRequest = {
      route: `charger/schedule/set`,
      request: {
        method: 'POST',
        entity: 'schedule',
        body: schedule,
      },
    };

    const response = await fetchDataFromAPITyped<
      ChargerSchedule | ChargerSchedule[]
    >(apiQuery);
    if (!response || !isSuccessful(response.status)) {
      notificationService.sendNotification('error', 'Failed to set schedule');
      return response.payload;
    } else {
      notificationService.sendNotification('success', 'Success!');
      return response.payload;
    }
  } catch (error) {
    console.error('Error while setting schedule', error);
    notificationService.sendNotification(
      'error',
      `Error while setting schedule: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return null;
  }
};

/**
 * Deletes one or more schedules.
 *
 * @param scheduleIds - A single schedule ID or an array of schedule IDs to delete.
 */
export const deleteSchedule = async (
  scheduleIds: string | string[]
): Promise<boolean> => {
  try {
    // Convert array to comma-separated string if needed.
    const scheduleIdsParam = Array.isArray(scheduleIds)
      ? scheduleIds.join(',')
      : scheduleIds;
    const apiQuery: ApiRequest = {
      route: `charger/schedule/delete/${scheduleIdsParam}`,
      request: {
        method: 'DELETE',
        entity: 'schedule',
        body: undefined,
      },
    };

    const response = await fetchDataFromAPITyped<DeleteSchedulesResponse>(
      apiQuery
    );
    if (!response || !isSuccessful(response.status)) {
      notificationService.sendNotification(
        'error',
        'Failed to delete schedule'
      );
      return false;
    } else {
      notificationService.sendNotification(
        'success',
        'Successfully removed schedule!'
      );
      return true;
    }
  } catch (error) {
    console.error('Error while deleting schedule', error);
    notificationService.sendNotification(
      'error',
      `Error while deleting schedule: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return false;
  }
};
